/**
 * We need to import this otherwise firebaseAppInstance.auth() will return us
 * an error `auth() is not a function`
 */
import 'firebase/auth';

import firebase from 'firebase/compat/app';
import React, { ReactNode, useCallback, useEffect, useState } from 'react';

import { FirebaseContext, FirebaseUser } from './context';
import { getUrlParam } from '../../utils';

interface Provider {
    children?: ReactNode;
}

export interface FirebaseAuthWithDataProps {
    type: Firebase.FormSubmitType;
    data: {
        [key: string]: any;
    };
}

export namespace Firebase {
    export type FormSubmitType =
        | 'firebase_login'
        | 'firebase_register'
        | 'firebase_reset'
        | 'firebase_reset_confirmation';
}

const getCurrentUser = () => {
    return new Promise((resolve, reject) => {
        const unsubscribe = firebase.auth().onAuthStateChanged((user) => {
            unsubscribe();
            resolve(user);
        }, reject);
    });
};

export const FirebaseProvider = ({ children }: Provider) => {
    /**
     * The currently signed-in user (or null).
     */
    const [user, setUser] = useState<FirebaseUser>(firebase.auth().currentUser);

    /**
     * Firebase provides `onAuthStateChanged` observer which we are using
     * to update user state.
     *
     * Since we are adding an observer to the application we need to use
     * cleanup function `() => unobserve()`
     */
    useEffect(() => {
        const unobserve = firebase.auth().onAuthStateChanged((user) => {
            setUser(user ? user : null);
        });
        return () => unobserve();
    });

    const userSignOut = useCallback(() => firebase.auth().signOut(), []);

    const firebaseFormSubmit = ({ type, data }: FirebaseAuthWithDataProps) => {
        switch (type) {
            case 'firebase_register':
                return firebase
                    .auth()
                    .createUserWithEmailAndPassword(data.email, data.password);
            case 'firebase_reset':
                return firebase.auth().sendPasswordResetEmail(data.email);
            case 'firebase_reset_confirmation':
                return firebase
                    .auth()
                    .confirmPasswordReset(
                        getUrlParam('oobCode'),
                        data.password,
                    );
            default:
            case 'firebase_login':
                return firebase
                    .auth()
                    .signInWithEmailAndPassword(data.email, data.password);
        }
    };

    return (
        <FirebaseContext.Provider
            value={{
                user,
                userSignOut,
                getCurrentUser,
                auth: firebase.auth(),
                firebaseFormSubmit,
            }}
        >
            {children}
        </FirebaseContext.Provider>
    );
};

export * from './context';
